import { useState, useEffect } from "react";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import {
  FaUserTie,
  FaStore,
  FaBuilding,
  FaEnvelope,
  FaMapMarkerAlt,
  FaUniversity,
  FaTags,
} from "react-icons/fa";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import "../styles/OnboardMerchantPage.css";
import { onboardMerchant, updateMerchant } from "../api/api"; // ✅ add updateMerchant API
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { Dropdown } from "primereact/dropdown";
import { FARE_TYPES,MERCHANT_TYPES } from "../utils/constants";
import { toDropdownOptions } from "../utils/enumUtils";
import { useLocation, useNavigate } from "react-router-dom";

// ✅ Validation Schema
const validationSchema = Yup.object().shape({
  merchantName: Yup.string()
    .matches(/^[A-Za-z\s]+$/, "Only letters and spaces allowed")
    .required("Merchant Name is required"),
  merchantId: Yup.string()
    .trim()
    .required("Merchant ID is required")
    .matches(/^\d+$/, "Merchant ID must contain digits only")
    .min(15, "Merchant ID cannot less then 15 digits")
    .max(15, "Merchant ID cannot exceed 15 digits"),
  bussinessName: Yup.string()
    .matches(/^[A-Za-z0-9\s.,-]+$/, "Invalid characters in Business Name")
    .required("Business Name is required"),
  email: Yup.string()
    .email("Invalid email format")
    .required("Email is required"),
  address: Yup.string()
    .matches(/^[A-Za-z0-9\s.,-]+$/, "Invalid characters in Address")
    .required("Address is required"),
  phoneNumber: Yup.string()
    .matches(/^[0-9]{10,15}$/, "Phone number must be 10–15 digits")
    .required("Phone number is required"),
  fareType: Yup.string().required("Fare Type is required"),
  IBAN: Yup.string()
    .matches(/^[A-Z]{2}[0-9A-Z]{13,32}$/, "Invalid IBAN format")
    .nullable()
    .notRequired(),

  merchantType: Yup.string().nullable().notRequired(),
});

const INITIAL_FORM = {
  merchantName: "",
  merchantId: "",
  bussinessName: "",
  email: "",
  address: "",
  phoneNumber: "92",
  fareType: "",
  IBAN: "",
  merchantType: "",
};

export default function OnboardMerchantPage() {
  const [loading, setLoading] = useState(false);
  const fareTypeOptions = toDropdownOptions(FARE_TYPES.KNOWN_FARE);

  const location = useLocation();
  const navigate = useNavigate();
  const mode = location.state?.mode || "create";
  const initialData = location.state?.merchant || {};

  const isEditMode = mode === "edit";
  const isViewMode = mode === "view";

  const formInitialValues =
    isEditMode || isViewMode
      ? {
          ...INITIAL_FORM,
          merchantName: initialData.MerchantName || "",
          merchantId: initialData.MID || "",
          bussinessName: initialData.BusinessName || "",
          email: initialData.Email || "",
          address: initialData.Address || "",
          phoneNumber: initialData.PhoneNumber
            ? initialData.PhoneNumber.replace("+", "")
            : "92",
          fareType: initialData.fareType || "",
          IBAN: initialData.IBAN || "",
          merchantType: initialData.merchantType || "",
        }
      : INITIAL_FORM;

  // ✅ Handle form submission
  const handleSubmit = async (values, { resetForm }) => {
    if (isViewMode) return; // No action in view mode

    setLoading(true);
    try {
      const payload = {
        MerchantName: values.merchantName,
        MID: values.merchantId,
        BusinessName: values.bussinessName,
        PhoneNumber: `+${values.phoneNumber}`,
        Email: values.email,
        Address: values.address,
        fareType: values.fareType,
        IBAN: values.IBAN || "",
        merchantType: values.merchantType || "",
      };

      const res = isEditMode
        ? await updateMerchant(payload)
        : await onboardMerchant(payload);

      if (res?.isSuccess) {
        alert(
          isEditMode
            ? "Merchant updated successfully!"
            : "Merchant onboarded successfully!",
        );

        navigate("/dashboard/merchantslist");
      } else {
        alert(res?.message || "Failed to save merchant!");
      }
    } catch (err) {
      console.error("Merchant Save Error:", err);
      alert(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="create-terminal-page">
      <div className="create-terminal-container">
        <Formik
          initialValues={formInitialValues}
          validationSchema={isViewMode ? null : validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ values, setFieldValue }) => (
            <Form className="create-terminal-form">
              {/* Merchant Name */}
              <div className="input-with-icon">
                <FaUserTie className="input-icon" />
                <Field
                  name="merchantName"
                  as={InputText}
                  placeholder="Merchant Name"
                  className="onboard-input"
                  disabled={isViewMode}
                  onChange={(e) => {
                    const onlyLetters = e.target.value.replace(
                      /[^A-Za-z\s]/g,
                      "",
                    );
                    setFieldValue("merchantName", onlyLetters);
                  }}
                />
              </div>
              <ErrorMessage
                name="merchantName"
                component="div"
                className="error-text"
              />

              {/* Merchant ID */}
              <div className="input-with-icon">
                <FaStore className="input-icon" />
                <Field
                  name="merchantId"
                  as={InputText}
                  placeholder="Merchant ID"
                  maxLength={15}
                  className="onboard-input"
                  disabled={isViewMode || isEditMode}
                  onChange={(e) => {
                    const onlyDigits = e.target.value.replace(/\D/g, "");
                    setFieldValue("merchantId", onlyDigits);
                  }}
                />
              </div>
              <ErrorMessage
                name="merchantId"
                component="div"
                className="error-text"
              />

              {/* Business Name */}
              <div className="input-with-icon">
                <FaBuilding className="input-icon" />
                <Field
                  name="bussinessName"
                  as={InputText}
                  placeholder="Business Name"
                  className="onboard-input"
                  disabled={isViewMode}
                />
              </div>
              <ErrorMessage
                name="bussinessName"
                component="div"
                className="error-text"
              />

              {/* Email */}
              <div className="input-with-icon">
                <FaEnvelope className="input-icon" />
                <Field
                  name="email"
                  as={InputText}
                  placeholder="Email"
                  className="onboard-input"
                  disabled={isViewMode || isEditMode}
                />
              </div>
              <ErrorMessage
                name="email"
                component="div"
                className="error-text"
              />
              <div className="input-with-icon">
                <FaUniversity className="input-icon" />
                <Field
                  name="IBAN"
                  as={InputText}
                  placeholder="IBAN"
                  className="onboard-input"
                  disabled={isViewMode}
                />
              </div>
              <ErrorMessage
                name="IBAN"
                component="div"
                className="error-text"
              />

              <div className="input-with-icon">
                <FaTags className="input-icon" />
                <Dropdown
                  name="merchantType"
                  value={values.merchantType}
                  options={toDropdownOptions(MERCHANT_TYPES.KNOWN_TYPES)}
                  optionLabel="label"
                  optionValue="value"
                  onChange={(e) => setFieldValue("merchantType", e.value)}
                  placeholder="Select Merchant Type"
                  className="onboard-input w-full"
                  filter
                  showClear
                  disabled={isViewMode}
                />
              </div>
              <ErrorMessage
                name="merchantType"
                component="div"
                className="error-text"
              />
              {/* Address */}
              <div className="input-with-icon">
                <FaMapMarkerAlt className="input-icon" />
                <Field
                  name="address"
                  as={InputText}
                  placeholder="Address"
                  className="onboard-input"
                  disabled={isViewMode}
                />
              </div>
              <ErrorMessage
                name="address"
                component="div"
                className="error-text"
              />

              {/* Phone Number */}
              <div className="input-with-icon">
                <PhoneInput
                  country="pk"
                  value={values.phoneNumber}
                  onChange={(value) => setFieldValue("phoneNumber", value)}
                  placeholder="Enter phone number"
                  containerClass="create-terminal__phone"
                  inputClass="create-terminal__phone-input"
                  enableSearch
                  countryCodeEditable={false}
                  disabled={isViewMode}
                />
              </div>
              <ErrorMessage
                name="phoneNumber"
                component="div"
                className="error-text"
              />

              {/* Fare Type Dropdown */}
              <div className="input-with-icon">
                <FaMapMarkerAlt className="input-icon" />
                <Dropdown
                  name="fareType"
                  value={values.fareType}
                  options={fareTypeOptions}
                  optionLabel="label"
                  optionValue="value"
                  onChange={(e) => setFieldValue("fareType", e.value)}
                  placeholder="Select Fare Type"
                  className="onboard-input w-full"
                  filter
                  showClear
                  filterBy="label"
                  disabled={isViewMode}
                />
              </div>
              <ErrorMessage
                name="fareType"
                component="div"
                className="error-text"
              />

              {/* Buttons */}

              {!isViewMode && (
                <Button
                  type="submit"
                  label={
                    loading ? "Saving..." : isEditMode ? "Update" : "Create"
                  }
                  disabled={loading}
                  className="create-terminal__submit"
                />
              )}
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
}
